import { lazy } from "react";

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function snakeCaseToTitleCase(string = "") {
  if (!string) return "";
  return string
    .split("_")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
}

export function convertToFilterableString(string = "") {
  if (!string) return "";
  return string
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/([^0-9a-z\s])/g, "");
}

export function isValidEmail(email) {
  let re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function openInNewTab(url) {
  window.open(url, "_blank");
}

export function directTo(url) {
  window.location.href = url;
}

export function dotifyText(str, maxLength) {
  if (str.length > maxLength) {
    return `${str.slice(0, maxLength)}...`;
  } else {
    return str;
  }
}

export function chunk(inputArray, perChunk) {
  let result = inputArray.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / perChunk);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []; // start a new chunk
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);
  return result;
}

export function abbrNumberConverter(number) {
  return String(number)
    .replace("M", " Million")
    .replace("B", " Billion")
    .replace("T", " Trillion"); //.replace('K', ' Thousand')
}

export function urlify(str) {
  return str
    .split(" ")
    .join("-")
    .toLowerCase()
    .replace(/[^a-zA-Z0-9-_]/g, "");
}

export function paramStringToObject(paramString) {
  let pairs = paramString.slice(1).split("&");
  let result = {};
  pairs.forEach((pair) => {
    const splitPair = pair.split("=");
    result[splitPair[0]] = decodeURIComponent(splitPair[1] || "");
  });
  return JSON.parse(JSON.stringify(result));
}

export function debounce(func, wait, immediate) {
  let timeout;
  return function () {
    let context = this,
      args = arguments;
    let later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

/**
 * Curried compare function to pass to native sort function to sort alphabetically
 * @param property {string} - property to sort on (optional)
 */
export const sortAlphabetically =
  (property) =>
  /**
   * @param a {any} - first item in comparator
   * @param b {any} - second item in comparator
   * @return {number} - number to indicate item index change based on comparison
   */
  (a, b) => {
    let aProperty = a;
    let bProperty = b;
    if (property) {
      aProperty = a[property];
      bProperty = b[property];
    }
    return aProperty < bProperty ? -1 : aProperty > bProperty ? 1 : 0;
  };

export const applySaltToChar = (code) =>
  textToChars("<img src='/getbaited.jpg' />").reduce((a, b) => a ^ b, code);

export function hexToRgb(hex, opacity = "0.15") {
  let h = hex.replace("#", "");
  h = h.match(new RegExp("(.{" + h.length / 3 + "})", "g"));

  for (let i = 0; i < h.length; i++)
    h[i] = parseInt(h[i].length == 1 ? h[i] + h[i] : h[i], 16);

  if (typeof opacity != "undefined") h.push(opacity);

  return "rgba(" + h.join(",") + ")";
}

export function shadeColor(color, percent) {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt((R * (100 + percent)) / 100);
  G = parseInt((G * (100 + percent)) / 100);
  B = parseInt((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  let RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
  let GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
  let BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
}

export const byteHex = (n) => ("0" + Number(n).toString(16)).substr(-2);

export function createParams(loginObject = {}, paramObject = {}) {
  let paramObj = paramObject;
  if (loginObject.authToken) paramObj["auth_token"] = loginObject.authToken;
  return Object.entries(paramObj)
    .map(([k, v]) => {
      return v || v === false || v === 0
        ? `${k}=${encodeURIComponent(v)}`
        : null;
    })
    .filter((n) => n)
    .join("&");
}

export const textToChars = (t) => t.split("").map((c) => c.charCodeAt(0));

export const numberWithCommas = (num) =>
  num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const durationToStr = (milliseconds) => {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / 1000 / 60 / 60) % 24);

  const formattedTime = [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0"),
  ].join(":");

  return formattedTime;
};

export const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem("page-has-been-force-refreshed") || "false"
    );

    try {
      const component = await componentImport();

      window.localStorage.setItem("page-has-been-force-refreshed", "false");

      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        window.localStorage.setItem("page-has-been-force-refreshed", "true");
        return window.location.reload();
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error;
    }
  });

// Computes the distance between two points using the Haversine formula
function haversineDistance(
  startLatitude,
  startLongitude,
  endLatitude,
  endLongitude
) {
  var R = 6371; // Radius of the earth in km
  var dLat = toRadians(endLatitude - startLatitude);
  var dLon = toRadians(endLongitude - startLongitude);

  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(startLatitude)) *
      Math.cos(toRadians(endLatitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c; // Distance in km

  return d;
}

export function fixBearing(bearing) {
  // Ensure angle is in [0, 360) range
  while (bearing < 0) {
    bearing += 360;
  }
  while (bearing >= 360) {
    bearing -= 360;
  }
  return bearing;
}

// Computes the current position of Raven
export function currentPosition({
  startLatitude,
  startLongitude,
  endLatitude,
  endLongitude,
  speed,
  startTime,
  currentTime,
}) {
  const usedStartTime =
    typeof startTime === "string" || startTime instanceof String
      ? new Date(startTime).getTime()
      : startTime;
  var distance = haversineDistance(
    startLatitude,
    startLongitude,
    endLatitude,
    endLongitude
  );

  var travelTime = distance / parseFloat(speed); // Total travel time

  var elapsedTime = (currentTime - usedStartTime) / (60 * 60 * 1000); // Time elapsed in hours
  var proportion = elapsedTime / travelTime;

  if (proportion > 1) {
    // The Raven has already reached its destination
    return { latitude: endLatitude, longitude: endLongitude, didArrive: true };
  } else {
    // The Raven is still in transit
    var latitude = startLatitude + proportion * (endLatitude - startLatitude);
    var longitude =
      startLongitude + proportion * (endLongitude - startLongitude);
    return { latitude, longitude };
  }
}

//removeDuplicates(data, 'id');
export function removeDuplicates(originalArray, prop) {
  const newArray = [];
  const lookupObject = {};

  for (const i in originalArray) {
    lookupObject[originalArray[i][prop]] = originalArray[i];
  }

  for (const i in lookupObject) {
    newArray.push(lookupObject[i]);
  }
  return newArray;
}

export function getSizeFromLatDelta(delta, minSize = 10, maxSize = 40) {
  // Points
  const maxDelta = 100;
  const minDelta = 0.001;

  if (delta >= maxDelta) return minSize;
  if (delta <= minDelta) return maxSize;

  // Solve for a and b using the two points
  const a = (maxSize - minSize) / (Math.log(minDelta) - Math.log(maxDelta));
  const b = maxDelta * Math.exp(minSize / a);

  // Solve for c using the first point
  const c = minSize - a * Math.log(maxDelta / b);

  // Function to calculate X based on DELTA
  return Math.floor(a * Math.log(delta / b) + c);
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

function toDegrees(radians) {
  return radians * (180 / Math.PI);
}
